import { baseApi } from "./baseApi";
import { getInstanceIdFromUrl } from "@/utils/instanceId";

/**
 * Offsets for the built-in journeys, in hours unless the key says days.
 * Negative is before the anchoring event, positive is after: an interview
 * reminder is -24, a post-interview thank-you is +1.
 */
export interface JourneyDefaults {
  interviewReminderHours: number[];
  postInterviewFollowUpHours: number[];
  inactivityHoldingDays: number[];
  offerChaseHours: number[];
  preJoiningDays: number[];
}

export interface CandidateEngagementSettings {
  isEnabled: boolean;
  genericStageLabel: string;
  defaultStaleAfterHours: number;
  maxAttempts: number;
  unsubscribeFooterEnabled: boolean;
  journeyDefaults: JourneyDefaults;
}

/**
 * The events a rule can trigger on.
 *
 * Must stay in step with ENGAGEMENT_EVENTS on the server: an event listed here
 * but never published gives HR a rule that cannot fire, and an event published
 * but missing here silently withholds a trigger they need. This list omitted
 * all three OFFER_* events while the server published them, so no offer rule
 * could be authored at all -- at the stage this codebase repeatedly calls the
 * most expensive drop-off in recruiting.
 */
export const ENGAGEMENT_EVENT_OPTIONS = [
  { value: "APPLICATION_RECEIVED", label: "Candidate applies" },
  { value: "STAGE_CHANGED", label: "Candidate moves stage" },
  { value: "INTERVIEW_SCHEDULED", label: "Interview scheduled" },
  { value: "INTERVIEW_RESCHEDULED", label: "Interview rescheduled" },
  { value: "INTERVIEW_CANCELLED", label: "Interview cancelled" },
  { value: "INTERVIEW_COMPLETED", label: "Interview completed" },
  { value: "OFFER_SENT", label: "Offer sent" },
  { value: "OFFER_ACCEPTED", label: "Offer accepted" },
  { value: "OFFER_DECLINED", label: "Offer declined" },
  { value: "CANDIDATE_REJECTED", label: "Candidate rejected" },
  { value: "CANDIDATE_HIRED", label: "Candidate hired" },
] as const;

/**
 * Only actions that actually reach someone.
 *
 * `notify_recruiter` and `create_task` were offered here and did nothing
 * useful: both wrote a row to job_applicant_notifications with
 * notification_type 'engagement_alert', which NO surface in the product reads —
 * the inbox and candidate timeline filter on a list that excludes it. No email
 * was sent, the recruiter address was never populated, and "create a task"
 * delegated to the same handler because there is no task table. The action
 * reported success and nothing happened.
 *
 * They come back when there is something on the other end to receive them.
 * Offering a control that silently does nothing is the failure this module was
 * built to end.
 */
export const RULE_ACTION_OPTIONS = [
  { value: "send_email", label: "Send email" },
  { value: "send_sms", label: "Send SMS" },
] as const;

export type RuleActionType = (typeof RULE_ACTION_OPTIONS)[number]["value"];

export interface RuleAction {
  type: RuleActionType;
  templateId?: string | null;
  delayMinutes?: number;
  staleAfterHours?: number;
}

export interface RuleConditions {
  jobIds?: string[];
  departments?: string[];
  locations?: string[];
  sources?: string[];
  employmentTypes?: string[];
  stages?: string[];
}

export interface AutomationRule {
  id: string;
  name: string;
  description: string | null;
  triggerType: string;
  triggerConfig: {
    eventType?: string;
    delayMinutes?: number;
    staleAfterHours?: number;
    conditions?: RuleConditions;
  } | null;
  actions: RuleAction[] | null;
  isActive: boolean;
  priority: number;
  lastTriggeredAt: string | null;
  triggerCount: number;
  createdAt: string;
  updatedAt: string;
}

export interface AutomationRuleInput {
  name: string;
  description?: string;
  triggerType: string;
  triggerConfig: AutomationRule["triggerConfig"];
  actions: RuleAction[];
  isActive: boolean;
  priority: number;
}

/** Server rows are snake_case; the rest of the app expects camelCase. */
interface RawRule {
  id: string;
  name: string;
  description: string | null;
  trigger_type: string;
  trigger_config: AutomationRule["triggerConfig"];
  actions: RuleAction[] | null;
  is_active: boolean;
  priority: number;
  last_triggered_at: string | null;
  trigger_count: number;
  created_at: string;
  updated_at: string;
}

const toRule = (r: RawRule): AutomationRule => ({
  id: r.id,
  name: r.name,
  description: r.description,
  triggerType: r.trigger_type,
  triggerConfig: r.trigger_config,
  actions: r.actions,
  isActive: r.is_active,
  priority: r.priority,
  lastTriggeredAt: r.last_triggered_at,
  triggerCount: r.trigger_count,
  createdAt: r.created_at,
  updatedAt: r.updated_at,
});

export const candidateEngagementApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    /**
     * Whether the feature is on for this instance.
     *
     * Separate from the settings query because the sidebar needs it for every
     * user, including those without settings.view who would get a 403 from the
     * settings endpoint and never see the nav entry even when it is enabled.
     */
    getEngagementEnabled: builder.query<boolean, void>({
      query: () => ({
        url: "/candidate-engagement/enabled",
        method: "GET",
        params: { instanceId: getInstanceIdFromUrl() },
      }),
      transformResponse: (r: { enabled: boolean }) => r.enabled,
      providesTags: ["CandidateEngagement"],
    }),

    getEngagementSettings: builder.query<CandidateEngagementSettings, void>({
      query: () => ({
        url: "/candidate-engagement/settings",
        method: "GET",
        params: { instanceId: getInstanceIdFromUrl() },
      }),
      providesTags: ["CandidateEngagement"],
    }),

    getAutomationRules: builder.query<AutomationRule[], void>({
      query: () => ({
        url: "/hiring/automation-rules",
        method: "GET",
        params: { instanceId: getInstanceIdFromUrl() },
      }),
      transformResponse: (r: { data: RawRule[] }) => (r.data ?? []).map(toRule),
      providesTags: ["CandidateEngagementRules"],
    }),

    createAutomationRule: builder.mutation<AutomationRule, AutomationRuleInput>({
      query: (body) => ({
        url: "/hiring/automation-rules",
        method: "POST",
        params: { instanceId: getInstanceIdFromUrl() },
        body,
      }),
      transformResponse: (r: { data: RawRule }) => toRule(r.data),
      invalidatesTags: ["CandidateEngagementRules"],
    }),

    updateAutomationRule: builder.mutation<
      AutomationRule,
      { id: string } & Partial<AutomationRuleInput>
    >({
      query: ({ id, ...body }) => ({
        url: `/hiring/automation-rules/${id}`,
        method: "PATCH",
        params: { instanceId: getInstanceIdFromUrl() },
        body,
      }),
      transformResponse: (r: { data: RawRule }) => toRule(r.data),
      invalidatesTags: ["CandidateEngagementRules"],
    }),

    deleteAutomationRule: builder.mutation<{ success: boolean }, string>({
      query: (id) => ({
        url: `/hiring/automation-rules/${id}`,
        method: "DELETE",
        params: { instanceId: getInstanceIdFromUrl() },
      }),
      invalidatesTags: ["CandidateEngagementRules"],
    }),

    /**
     * Saves a PATCH, not the whole object, so toggling one switch cannot reset
     * another that a second admin changed in the meantime.
     */
    updateEngagementSettings: builder.mutation<
      CandidateEngagementSettings,
      Partial<CandidateEngagementSettings>
    >({
      query: (patch) => ({
        url: "/candidate-engagement/settings",
        method: "PUT",
        params: { instanceId: getInstanceIdFromUrl() },
        body: patch,
      }),
      invalidatesTags: ["CandidateEngagement"],
    }),
  }),
});

export const {
  useGetEngagementEnabledQuery,
  useGetEngagementSettingsQuery,
  useUpdateEngagementSettingsMutation,
  useGetAutomationRulesQuery,
  useCreateAutomationRuleMutation,
  useUpdateAutomationRuleMutation,
  useDeleteAutomationRuleMutation,
} = candidateEngagementApi;
